import { createAction } from "@reduxjs/toolkit";
import { Pageable } from "../../services/pageable";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { VaultsDTO } from "./models/dto";

export namespace VaultActions {
  export enum ActionTypes {
    getAllVaults = "Vaults: Get All Vaults",
    setAllVaults = "Vaults: Set All Vaults",
    resetAllVaults = "Vaults: Reset All Vaults",
    getSingleVaults = "Vaults: Get Single Vaults",
    setSingleVaults = "Vaults: Set Single Vaults",
    setAllVaultsPagination = "Vaults: Set All Vaults Pagination",

    getAllVaultEntries = "Vaults: Get All Vault Entries",
    setAllVaultEntries = "Vaults: Set All Vault Entries",
    resetAllVaultEntries = "Vaults: Reset All Vault Entries",
    getSingleVaultEntries = "Vaults: Get Single Vault Entries",
    setSingleVaultEntries = "Vaults: Set Single Vault Entries",
    setAllVaultEntriesPagination = "Vaults: Set All Vault Entries Pagination",
  }

  // Actions Types
  export const resetAllVaults = createAction(ActionTypes.resetAllVaults);

  export const getAllVaults = createAction(ActionTypes.getAllVaults);

  export const setAllVaults = createAction<
    FetchableSuccess<VaultsDTO.Responses.GetAllVaults.T> | FetchableError,
    ActionTypes.setAllVaults
  >(ActionTypes.setAllVaults);

  export const setAllVaultsPagination = createAction<
    Pageable.T<keyof VaultsDTO.Responses.GetSingleVault.T>,
    ActionTypes.setAllVaultsPagination
  >(ActionTypes.setAllVaultsPagination);

  export const getSingleVaults = createAction<
    VaultsDTO.Responses.GetSingleVault.T["id"],
    ActionTypes.getSingleVaults
  >(ActionTypes.getSingleVaults);

  export const setSingleVaults = createAction<
    { id: string; result: VaultsDTO.Responses.GetSingleVault.T },
    ActionTypes.setSingleVaults
  >(ActionTypes.setSingleVaults);

  export const resetAllVaultEntries = createAction<
    VaultsDTO.Responses.GetSingleVault.T["id"],
    ActionTypes.resetAllVaultEntries
  >(ActionTypes.resetAllVaultEntries);

  export const getAllVaultEntries = createAction<
    VaultsDTO.Responses.GetSingleVault.T["id"],
    ActionTypes.getAllVaultEntries
  >(ActionTypes.getAllVaultEntries);

  export const setAllVaultEntries = createAction<
    {
      entries:
        | FetchableSuccess<VaultsDTO.Responses.GetAllEntries.T>
        | FetchableError;
      vaultId: VaultsDTO.Responses.GetSingleVault.T["id"];
    },
    ActionTypes.setAllVaultEntries
  >(ActionTypes.setAllVaultEntries);

  export const setAllVaultEntriesPagination = createAction<
    {
      pagination: Pageable.T<keyof VaultsDTO.Responses.GetSingleEntry.T>;
      vaultId: VaultsDTO.Responses.GetSingleVault.T["id"];
    },
    ActionTypes.setAllVaultEntriesPagination
  >(ActionTypes.setAllVaultEntriesPagination);

  export const getSingleVaultEntries = createAction<
    {
      entryId: VaultsDTO.Responses.GetSingleEntry.T["id"];
      vaultId: VaultsDTO.Responses.GetSingleVault.T["id"];
    },
    ActionTypes.getSingleVaultEntries
  >(ActionTypes.getSingleVaultEntries);

  export const setSingleVaultEntries = createAction<
    {
      entryId: VaultsDTO.Responses.GetSingleEntry.T["id"];
      vaultId: VaultsDTO.Responses.GetSingleVault.T["id"];
      result: VaultsDTO.Responses.GetSingleVault.T;
    },
    ActionTypes.setSingleVaultEntries
  >(ActionTypes.setSingleVaultEntries);

  export type GetAllVaults = ReturnType<typeof getAllVaults>;
  export type SetAllVaults = ReturnType<typeof setAllVaults>;
  export type GetSingleVaults = ReturnType<typeof getSingleVaults>;
  export type SetSingleVaults = ReturnType<typeof setSingleVaults>;
  export type ResetAllVaults = ReturnType<typeof resetAllVaults>;
  export type SetAllVaultsPagination = ReturnType<
    typeof setAllVaultsPagination
  >;
  export type GetAllVaultEntries = ReturnType<typeof getAllVaultEntries>;
  export type SetAllVaultEntries = ReturnType<typeof setAllVaultEntries>;
  export type GetSingleVaultEntries = ReturnType<typeof getSingleVaultEntries>;
  export type SetSingleVaultEntries = ReturnType<typeof setSingleVaultEntries>;
  export type ResetAllVaultEntries = ReturnType<typeof resetAllVaultEntries>;
  export type SetAllVaultEntriesPagination = ReturnType<
    typeof setAllVaultEntriesPagination
  >;

  export type Actions =
    // ---
    | GetAllVaults
    | SetAllVaults
    | GetSingleVaults
    | SetSingleVaults
    | ResetAllVaults
    | SetAllVaultsPagination
    | GetAllVaultEntries
    | SetAllVaultEntries
    | GetSingleVaultEntries
    | SetSingleVaultEntries
    | ResetAllVaultEntries
    | SetAllVaultEntriesPagination;
}
